import { Box, Grid, TextField, MenuItem, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import {
  ReplaceMatchMode,
  TransformerType,
  TRANSFORMER_TYPE_ORDER,
} from "@/constants/scraper";
import type { TransformerFormState } from "@/scraper-form/transformerState";

interface TransformerFieldsRowProps {
  row: TransformerFormState;
  onPatch: (patch: Partial<TransformerFormState>) => void;
  onRemove: () => void;
  onResetType: (nextType: string) => void;
}

export function TransformerFieldsRow({
  row,
  onPatch,
  onRemove,
  onResetType,
}: TransformerFieldsRowProps) {
  const { t } = useTranslation();
  const tr = row;

  return (
    <Box
      sx={{
        pl: 2,
        ml: 0.5,
        borderLeft: 2,
        borderColor: "divider",
        mb: 2,
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          alignItems: "flex-start",
        }}
      >
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth
            size="small"
            select
            label={t("scraperForm.transformerType")}
            value={tr.type}
            onChange={(e) => onResetType(e.target.value)}
          >
            {TRANSFORMER_TYPE_ORDER.map((tt) => (
              <MenuItem key={tt} value={tt}>
                {tt}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {tr.type === TransformerType.Replace && (
          <>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={t("scraperForm.replaceWhat")}
                value={tr.what}
                onChange={(e) => onPatch({ what: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                size="small"
                label={t("scraperForm.replaceWith")}
                value={tr.with}
                onChange={(e) => onPatch({ with: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t("scraperForm.replaceOccurrence")}
                value={
                  tr.occurence === ReplaceMatchMode.First
                    ? ReplaceMatchMode.First
                    : ReplaceMatchMode.All
                }
                onChange={(e) =>
                  onPatch({
                    occurence:
                      e.target.value === ReplaceMatchMode.First
                        ? ReplaceMatchMode.First
                        : ReplaceMatchMode.All,
                  })
                }
              >
                <MenuItem value={ReplaceMatchMode.First}>
                  {t("scraperForm.replaceFirst")}
                </MenuItem>
                <MenuItem value={ReplaceMatchMode.All}>
                  {t("scraperForm.replaceAllRegex")}
                </MenuItem>
              </TextField>
            </Grid>
          </>
        )}
        {(tr.type === TransformerType.Substring ||
          tr.type === TransformerType.Slice) && (
          <>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t("scraperForm.numericFrom")}
                value={tr.from}
                onChange={(e) => onPatch({ from: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t("scraperForm.numericTo")}
                value={tr.to}
                onChange={(e) => onPatch({ to: e.target.value })}
              />
            </Grid>
          </>
        )}
        {tr.type === TransformerType.Split && (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t("scraperForm.splitDelimiter")}
                value={tr.value}
                onChange={(e) => onPatch({ value: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t("scraperForm.splitIndex")}
                value={tr.index}
                onChange={(e) => onPatch({ index: e.target.value })}
              />
            </Grid>
          </>
        )}
        {(tr.type === TransformerType.PadEnd ||
          tr.type === TransformerType.PadStart) && (
          <>
            <Grid size={{ xs: 6, sm: 2 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={t("scraperForm.padLength")}
                value={tr.index}
                onChange={(e) => onPatch({ index: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <TextField
                fullWidth
                size="small"
                label={t("scraperForm.padChars")}
                value={tr.value}
                onChange={(e) => onPatch({ value: e.target.value })}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, sm: 1 }}>
          <IconButton
            aria-label={t("scraperForm.removeTransformer")}
            onClick={onRemove}
          >
            <DeleteIcon />
          </IconButton>
        </Grid>
      </Grid>
    </Box>
  );
}
